import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { resolveIdentityMode } from './api/client';
import type { ViewerResponse } from './api/types';
import { useApiResource } from './hooks/useApiResource';
import { authClient, useSession, signInWithPassword, verifyTotp, signOut } from './auth/localAuth';
import { DashboardPage } from './routes/DashboardPage';
import { DirectoryPage } from './routes/DirectoryPage';
import { TalentPage } from './routes/TalentPage';
import { EmployeeProfilePage } from './routes/EmployeeProfilePage';
import { RolesPage } from './routes/RolesPage';
import { DepartmentsPage } from './routes/DepartmentsPage';
import { RoleDetailPage } from './routes/RoleDetailPage';
import { OrganisationPage } from './routes/OrganisationPage';
import { AssessmentsQueuePage } from './routes/AssessmentsQueuePage';
import { PeerAssessmentsPage } from './routes/PeerAssessmentsPage';
import { DirectReportAssessmentPage } from './routes/DirectReportAssessmentPage';
import { ErrorState, LoadingState } from './components/StateMessage';
import styles from './App.module.css';

/**
 * Routing and the application shell.
 *
 * The nav lists exactly the destinations that exist and work: Dashboard
 * (Mission 017), People (Mission 010), Roles (Mission 013), Assessments
 * (the assessments-queue increment — `docs/DOMAIN_DECISIONS.md` "Assessment
 * Cadence Source and Queue Derivation"), Peer Assessments (the peer
 * visibility read-UI increment — `docs/DOMAIN_DECISIONS.md` "Peer
 * Visibility Scope") and Assess My Manager (`docs/DOMAIN_DECISIONS.md`
 * "Direct-Report Assessment Source" — the same caller-scoped, no-target-
 * parameter shape as Peer Assessments, for the direct_report source type).
 * docs/UX_ARCHITECTURE.md §2 lists Teams as well — `docs/DOMAIN_DECISIONS.md`
 * "Teams Roster Scope" (TR-1) originally judged the Dashboard's own roster
 * widget sufficient and declined a dedicated destination. Mission 162
 * revisited that: the widget alone gave no way to go *from* a department
 * or team *to* who is currently on it, so **Departments** (`/departments`)
 * now exists as that browsing destination — see this mission's own
 * `docs/DOMAIN_DECISIONS.md` entry for why TR-1 is superseded rather than
 * silently ignored. A nav item leading to a placeholder advertises a
 * product that is not there — a destination earns its link by being built,
 * as Dashboard did in Mission 017, Assessments does here, and **Talent**
 * does in the product-maturation mission that built `TalentPage.tsx`
 * (`docs/API.md` §5.21, `docs/FRONTEND.md` §5r): §2's own cross-employee
 * "Talent designations, potential/readiness views" destination, placed
 * after Roles to match §2's own ordering. Peer
 * Assessments is kept as its own destination rather than folded into
 * Assessments: the two read from different endpoints with different
 * scoping (organisation/responsibility/team cadence vs. caller-scoped peer
 * eligibility), and combining them on one page would either blur that
 * distinction or break the Assessments Queue's own "asks the server for
 * the queue, and only the queue" contract. Development and Career have no
 * nav entry of their own either: both are basic capture reachable from
 * within a profile, not a dedicated destination (`docs/DOMAIN_DECISIONS.md`
 * "Development and Career Aspiration Capture Scope" DC-4).
 *
 * `/` still lands on People. §2 lists Dashboard first, but nothing in the
 * documentation says which destination a session should open on, and
 * changing where everyone arrives is a product decision rather than a
 * consequence of building the page.
 *
 * **Organisation is deliberately not in that nav** (Mission 018). §2 places
 * it as "an admin area, not a top-level nav item competing with the
 * person-centric items", so it sits apart in the top bar and appears only
 * for an administrator — as the server describes them, via `GET /me`,
 * rather than as the browser guesses. That is a decision about what to
 * *show*: the route and every endpoint behind it check authorization
 * themselves, so hiding the link is a courtesy and never the control.
 *
 * ---------------------------------------------------------------------
 * TWO IDENTITY MODES
 *
 * `resolveIdentityMode()` (`api/client.ts`) decides once, from build-time
 * configuration, whether this instance is running in dev mode (an
 * unsigned identity assertion, local development only) or local mode (a
 * genuine EmployeeProfile-owned password + TOTP sign-in —
 * `docs/AUTHENTICATION_REASSESSMENT.md`, replacing the Entra/MSAL flow
 * this component previously rendered). Dev mode's own render path is
 * byte-for-byte what it always was, so every existing test that configures
 * a dev identity exercises exactly the same code.
 * ---------------------------------------------------------------------
 */
export function App() {
  const identityMode = resolveIdentityMode();

  if (identityMode.kind === 'dev') {
    return <Shell envBadge={<span className={styles.env}>Development mode</span>} />;
  }

  return <LocalAuthGate />;
}

/**
 * EmployeeProfile-owned sign-in gate: password, then (once the signed-in
 * account has TOTP enabled) a TOTP challenge, then the same `Shell` dev
 * mode renders, with a "signed in as" badge and a sign-out control in
 * place of the "Development mode" one. An account signed in without TOTP
 * enabled yet is routed to mandatory enrollment before the application
 * renders at all — this product's own two-layer login policy
 * (`docs/AUTHENTICATION.md`), not merely an optional add-on a user could
 * skip past.
 *
 * Deliberately a separate component from `App()` itself: it is the only
 * place in this file with session-dependent, multi-step state, and
 * keeping it apart means dev mode's own render path above has none at
 * all.
 */
function LocalAuthGate() {
  const { data: session, isPending } = useSession();
  const [pendingTotp, setPendingTotp] = useState(false);

  if (isPending) {
    return (
      <div className={styles.shell}>
        <main className={styles.main}>
          <LoadingState label="Checking sign-in status…" />
        </main>
      </div>
    );
  }

  if (!session && !pendingTotp) {
    return (
      <div className={styles.shell}>
        <main className={styles.main}>
          <LoginForm onTotpRequired={() => setPendingTotp(true)} />
        </main>
      </div>
    );
  }

  if (!session && pendingTotp) {
    return (
      <div className={styles.shell}>
        <main className={styles.main}>
          <TotpChallengeForm onBack={() => setPendingTotp(false)} />
        </main>
      </div>
    );
  }

  if (session && !session.user.twoFactorEnabled) {
    return (
      <div className={styles.shell}>
        <main className={styles.main}>
          <MfaEnrollmentForm />
        </main>
      </div>
    );
  }

  return (
    <Shell
      envBadge={
        <span className={styles.env} title={session?.user.email}>
          {session?.user.name ?? session?.user.email ?? 'Signed in'}
          <button type="button" className={styles.signOutButton} onClick={() => void signOut()}>
            Sign out
          </button>
        </span>
      }
    />
  );
}

/** Deliberately generic on failure — never discloses whether the email
 * exists (`docs/AUTHENTICATION.md`'s account-enumeration note; the same
 * message `../auth/localAuth.ts` already returns for every credential
 * failure). */
function LoginForm({ onTotpRequired }: { onTotpRequired: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await signInWithPassword(email, password);
    setSubmitting(false);
    if (result.kind === 'error') {
      setError(result.message);
      return;
    }
    if (result.kind === 'totp-required') {
      onTotpRequired();
    }
    // 'signed-in': useSession() picks up the new session reactively; no
    // local state change needed here.
  };

  return (
    <form className={styles.signInPrompt} onSubmit={(e) => void handleSubmit(e)}>
      <p className={styles.product}>Employee Profile</p>
      <div className={styles.field}>
        <label htmlFor="login-email">Email</label>
        <input
          id="login-email"
          className={styles.input}
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className={styles.field}>
        <label htmlFor="login-password">Password</label>
        <input
          id="login-password"
          className={styles.input}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      {error && <p className={styles.errorText}>{error}</p>}
      <button type="submit" className={styles.signInButton} disabled={submitting}>
        {submitting ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  );
}

function TotpChallengeForm({ onBack }: { onBack: () => void }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await verifyTotp(code);
    setSubmitting(false);
    if (result.kind === 'error') {
      setError(result.message);
      return;
    }
    // 'signed-in': useSession() picks up the new session reactively.
  };

  return (
    <form className={styles.signInPrompt} onSubmit={(e) => void handleSubmit(e)}>
      <p className={styles.product}>Two-factor authentication</p>
      <p>Enter your authenticator code.</p>
      <div className={styles.field}>
        <label htmlFor="totp-code">Authenticator code</label>
        <input
          id="totp-code"
          className={styles.input}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </div>
      {error && <p className={styles.errorText}>{error}</p>}
      <button type="submit" className={styles.signInButton} disabled={submitting}>
        {submitting ? 'Verifying…' : 'Verify'}
      </button>
      <button type="button" className={styles.signOutButton} onClick={onBack}>
        Back to sign in
      </button>
    </form>
  );
}

/**
 * Mandatory MFA enrollment for an account signed in without TOTP enabled
 * yet — this product's own policy is two-layer login always, not an
 * optional hardening step a signed-in user could simply never complete
 * (`docs/AUTHENTICATION.md`). Requires the password again (Better Auth's
 * own `twoFactor.enable` requirement for a credential account) rather
 * than trusting the still-fresh session alone to authorize a sensitive
 * change to the account.
 */
function MfaEnrollmentForm() {
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'password' | 'verify'>('password');
  const [secret, setSecret] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleEnable = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const { data, error: enableError } = await authClient.twoFactor.enable({ password, method: 'totp' });
    setSubmitting(false);
    // `data.method` narrows the response's discriminated union — 'otp'
    // (email OTP, a different method this application never requests) has
    // no `totpURI`/`backupCodes` at all; only ever reachable here if the
    // client library's own response shape changed unexpectedly.
    if (enableError || !data || data.method !== 'totp') {
      setError('Could not start MFA enrollment. Check your password and try again.');
      return;
    }
    const parsed = (() => {
      try {
        return new URL(data.totpURI).searchParams.get('secret');
      } catch {
        return null;
      }
    })();
    setSecret(parsed);
    setBackupCodes(data.backupCodes ?? []);
    setStep('verify');
  };

  const handleVerify = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const { error: verifyError } = await authClient.twoFactor.verifyTotp({ code });
    setSubmitting(false);
    if (verifyError) {
      setError('That code was not accepted. Check your authenticator app and try again.');
      return;
    }
    // Activated: useSession() will report twoFactorEnabled on its next
    // read, moving LocalAuthGate past this screen.
  };

  if (step === 'password') {
    return (
      <form className={styles.signInPrompt} onSubmit={(e) => void handleEnable(e)}>
        <p className={styles.product}>Set up two-factor authentication</p>
        <p>This account requires an authenticator app before you can continue. Confirm your password to begin.</p>
        <div className={styles.field}>
          <label htmlFor="mfa-password">Password</label>
          <input
            id="mfa-password"
            className={styles.input}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && <p className={styles.errorText}>{error}</p>}
        <button type="submit" className={styles.signInButton} disabled={submitting}>
          {submitting ? 'Starting…' : 'Continue'}
        </button>
      </form>
    );
  }

  return (
    <form className={styles.signInPrompt} onSubmit={(e) => void handleVerify(e)}>
      <p className={styles.product}>Set up two-factor authentication</p>
      <p>Add this key to your authenticator app, then enter the code it generates.</p>
      {secret && <p className={styles.secretKey}>{secret}</p>}
      {backupCodes.length > 0 && (
        <>
          <p>Save these recovery codes somewhere safe. Each can be used once if you lose access to your authenticator.</p>
          <div className={styles.backupCodes}>
            {backupCodes.map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
        </>
      )}
      <div className={styles.field}>
        <label htmlFor="mfa-verify-code">Authenticator code</label>
        <input
          id="mfa-verify-code"
          className={styles.input}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </div>
      {error && <p className={styles.errorText}>{error}</p>}
      <button type="submit" className={styles.signInButton} disabled={submitting}>
        {submitting ? 'Verifying…' : 'Activate'}
      </button>
    </form>
  );
}

/** The nav, routes and page content — identical regardless of identity
 * mode. `envBadge` is the one thing that differs: which identity is
 * active, and how to end the session. */
function Shell({ envBadge }: { envBadge: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // A narrow viewport collapses the nav behind a toggle (§18); closing it on
  // every navigation keeps a forward/back or a link click from leaving a
  // full-screen menu open over the page it just left.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className={styles.shell}>
      <header className={styles.topbar}>
        <span className={styles.product}>Employee Profile</span>
        <button
          type="button"
          className={styles.menuToggle}
          aria-expanded={menuOpen}
          aria-controls="primary-nav"
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? 'Close' : 'Menu'}
        </button>
        <div id="primary-nav" className={`${styles.navGroup} ${menuOpen ? styles.navGroupOpen : ''}`}>
          <nav className={styles.nav} aria-label="Main">
            <NavItem to="/dashboard">Dashboard</NavItem>
            <NavItem to="/people">People</NavItem>
            <NavItem to="/roles">Roles</NavItem>
            <NavItem to="/departments">Departments</NavItem>
            <NavItem to="/talent">Talent</NavItem>
            <NavItem to="/assessments">Assessments</NavItem>
            <NavItem to="/peer-assessments">Peer Assessments</NavItem>
            <NavItem to="/direct-report-assessment">Assess My Manager</NavItem>
          </nav>
          <AdminEntry />
          {envBadge}
        </div>
      </header>

      <main className={styles.main}>
        <Routes>
          <Route path="/" element={<Navigate to="/people" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/people" element={<DirectoryPage />} />
          {/* The trailing wildcard is the Snapshot/Detailed mode split
              (Mission 171, "Snapshot + Detailed Profile Experience") —
              `/people/:id` and `/people/:id/detailed` are the same route
              match, so `EmployeeProfilePage` itself (not a second Route
              entry) decides the mode from the URL. See that file's own
              header comment for why: a second Route entry pointing at the
              same element would remount it on every mode switch, discarding
              every already-loaded resource and re-issuing every request —
              exactly the "avoid duplicate API requests" this mission asked
              to avoid. */}
          <Route path="/people/:employeeId/*" element={<EmployeeProfilePage />} />
          <Route path="/roles" element={<RolesPage />} />
          <Route path="/departments" element={<DepartmentsPage />} />
          <Route path="/roles/:roleId" element={<RoleDetailPage />} />
          <Route path="/talent" element={<TalentPage />} />
          <Route path="/assessments" element={<AssessmentsQueuePage />} />
          <Route path="/peer-assessments" element={<PeerAssessmentsPage />} />
          <Route path="/direct-report-assessment" element={<DirectReportAssessmentPage />} />
          <Route path="/organisation" element={<OrganisationPage />} />
          <Route
            path="*"
            element={
              <ErrorState
                title="Page not found"
                detail="That address does not match anything in this application."
              />
            }
          />
        </Routes>
      </main>
    </div>
  );
}

/**
 * The way into the admin area, for the people it is for.
 *
 * Rendered outside the main nav because `docs/UX_ARCHITECTURE.md` §2 asked
 * for that explicitly, and rendered at all only when the server says the
 * caller is an administrator. It fails closed and silently: if `/me` cannot
 * be read, no link appears, which is the right way round — an absent link
 * costs an admin one typed URL, while a link shown to everyone would
 * advertise a surface almost nobody may use.
 */
function AdminEntry() {
  const viewer = useApiResource<ViewerResponse>('/me');
  if (viewer.status !== 'loaded' || !viewer.data.isAdministrator) return null;

  return (
    <NavLink to="/organisation" className={styles.navItem}>
      Organisation
    </NavLink>
  );
}

/**
 * A nav link that knows whether it is the current destination.
 *
 * `NavLink` sets `aria-current="page"` on the active link itself, which is
 * both what assistive technology reads and what App.module.css already
 * styles against. The previous hardcoded `aria-current` on the single
 * People item could not survive a second destination — it would have
 * claimed People was current while the reader was on Roles.
 */
function NavItem({ to, children }: { to: string; children: string }) {
  return (
    <NavLink to={to} className={styles.navItem}>
      {children}
    </NavLink>
  );
}
